import * as React from 'react'
import {
  subscribeToTokenChanges,
  getAuthTokenFromCookie,
  type AuthToken,
} from '../utils/tokenManager'

interface TokenContextValue {
  accessToken: string | null
  isAuthenticated: boolean
}

const TokenContext = React.createContext<TokenContextValue>({
  accessToken: null,
  isAuthenticated: false,
})

TokenContext.displayName = 'TokenContext'

/**
 * Token Provider that subscribes to token changes from tokenManager
 * This ensures React components re-render when tokens are refreshed
 */
export function TokenProvider(props: { children: React.ReactNode }) {
  const [authToken, setAuthToken] = React.useState<AuthToken | null>(() =>
    getAuthTokenFromCookie()
  )

  React.useEffect(() => {
    // Subscribe to token changes
    const unsubscribe = subscribeToTokenChanges((token) => {
      setAuthToken(token)
    })

    // Cleanup subscription on unmount
    return unsubscribe
  }, [])

  const value = React.useMemo(
    () => ({
      accessToken: authToken?.accessToken || null,
      isAuthenticated: !!authToken?.accessToken,
    }),
    [authToken]
  )

  return <TokenContext.Provider value={value} {...props} />
}

/**
 * Hook to access the current token context
 * Provides reactive access to authentication token
 */
export function useTokenContext(): TokenContextValue {
  const context = React.useContext(TokenContext)
  if (context === undefined) {
    throw new Error('useTokenContext must be used within a TokenProvider')
  }
  return context
}

